// src/api/schemas.ts

import { createAuthenticatedFetch } from '@/api/types/core/common';
import { sections } from './sections';
import type { Schema, SchemaCreate, SchemaUpdate } from './types/core/schemas';
import type { Section } from './types/core/sections';

// @deprecated Use imports from '@/api/types' instead
export type {
    Schema,
    SchemaCreate,
    SchemaUpdate
};

const api = createAuthenticatedFetch();

// Objet principal contenant toutes les fonctions
export const schemas = {
  async createSchema(schemaData: SchemaCreate): Promise<Schema> {
    try {
      return await api.post('/schemas/', schemaData);
    } catch (error) {
      throw new Error("Erreur lors de la création du schéma");
    }
  },

  async fetchSchemaById(uuid: string): Promise<Schema> {
    try {
      return await api.get(`/schemas/by_id/${uuid}`);
    } catch (error) {
      throw new Error("Erreur lors de la récupération du schéma");
    }
  },

  async updateSchema(uuid: string, schemaData: SchemaUpdate): Promise<Schema> {
    try {
      return await api.put(`/schemas/by_id/${uuid}`, schemaData);
    } catch (error) {
      throw new Error("Erreur lors de la mise à jour du schéma");
    }
  },

  async deleteSchema(uuid: string): Promise<void> {
    try {
      await api.delete(`/schemas/by_id/${uuid}`);
    } catch (error) {
      throw new Error("Erreur lors de la suppression du schéma");
    }
  },

  async fetchAllSchemas(): Promise<Schema[]> {
    try {
      return await api.get('/schemas/all');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des schémas");
    }
  },

  async getAccessible(): Promise<Schema[]> {
    try {
      return await api.get('/schemas/accessible');
    } catch (error) {
      throw new Error("Erreur lors de la récupération des schémas accessibles");
    }
  },

  async addSectionToSchema(uuid: string, sectionId: number): Promise<Schema> {
    try {
      return await api.post(`/schemas/${uuid}/sections/${sectionId}`, { section_id: sectionId });
    } catch (error) {
      throw new Error("Erreur lors de l'ajout de la section au schéma");
    }
  },

  async fetchSectionsBySchema(uuid: string): Promise<Section[]> {
    try {
      // 1. Récupérer la liste des associations schéma-section
      const schemaDataList = await api.get(`/schemas/${uuid}/sections`) as { section_id: number }[];
      const sectionIds = schemaDataList.map((sd: { section_id: number }) => sd.section_id);

      // 2. Récupérer toutes les sections
      const allSections = await sections.fetchAllSections();

      // 3. Filtrer les sections concernées
      return allSections.filter((section: Section) => sectionIds.includes(section.id));
    } catch (error) {
      throw new Error("Erreur lors de la récupération des sections du schéma");
    }
  },

  async removeSectionFromSchema(uuid: string, sectionId: number): Promise<Schema> {
    try {
      await api.delete(`/schemas/${uuid}/sections/${sectionId}`);
      // Retourner le schéma mis à jour
      return await this.fetchSchemaById(uuid);
    } catch (error) {
      throw new Error("Erreur lors de la suppression de la section du schéma");
    }
  }
};

// Exports des fonctions individuelles pour la rétrocompatibilité
export const createSchema = (schemaData: SchemaCreate): Promise<Schema> => schemas.createSchema(schemaData);
export const fetchSchemaById = (uuid: string): Promise<Schema> => schemas.fetchSchemaById(uuid);
export const updateSchema = (uuid: string, schemaData: SchemaUpdate): Promise<Schema> => schemas.updateSchema(uuid, schemaData);
export const deleteSchema = (uuid: string): Promise<void> => schemas.deleteSchema(uuid);
export const fetchAllSchemas = (): Promise<Schema[]> => schemas.fetchAllSchemas();
export const addSectionToSchema = (uuid: string, sectionId: number): Promise<Schema> => schemas.addSectionToSchema(uuid, sectionId);
export const fetchSectionsBySchema = (uuid: string): Promise<Section[]> => schemas.fetchSectionsBySchema(uuid);
export const removeSectionFromSchema = (uuid: string, sectionId: number): Promise<Schema> => schemas.removeSectionFromSchema(uuid, sectionId);